import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Play } from "@phosphor-icons/react";

/**
 * Landing-page hero teaser carousel — auto-rotates 6 marketing screenshots
 * with caption + chapter eyebrow, and a CTA to the full /tour page.
 *
 * Implemented with stacked layers + opacity transition (no framer-motion) to
 * guarantee bullet-proof rendering across browsers.
 */

const FILE = (n) => `/api/marketing/file/${n}`;

const SLIDES = [
    { img: "01-storefront-hero-desktop.jpg",       eyebrow: "01 · Donor arrives",         caption: "Branded storefront with live countdown" },
    { img: "32-silent-auction-bid-sheet.jpg",      eyebrow: "02 · Silent auction · table sheets", caption: "Printed bid sheet · QR jumps to THIS lot, no scrolling the catalog" },
    { img: "23-item-bidding-history-logged-in.jpg", eyebrow: "03 · Silent auction · mobile bidding", caption: "Donors bid from their phone · anti-snipe + outbid SMS" },
    { img: "16-admin-labels-avery-sheet.jpg",      eyebrow: "04 · Pre-event",             caption: "Print Avery 5163 paddle labels in one click" },
    { img: "17-admin-door-admission.jpg",          eyebrow: "05 · Door admission",        caption: "2-second admit · zero duplicates" },
    { img: "14-admin-pos-paddle-cart.jpg",         eyebrow: "06 · Floor POS",             caption: "Every volunteer becomes a register" },
    { img: "24-live-scoreboard-desktop.jpg",       eyebrow: "07 · Live auction + paddle raise", caption: "Big-screen scoreboard · live-auctioneer thermometer & paddle ticker" },
];

const AUTOPLAY_MS = 4200;

export default function SeeItInActionCarousel() {
    const [i, setI] = useState(0);
    const [paused, setPaused] = useState(false);

    useEffect(() => {
        if (paused) return;
        const t = setTimeout(() => setI((x) => (x + 1) % SLIDES.length), AUTOPLAY_MS);
        return () => clearTimeout(t);
    }, [i, paused]);

    return (
        <section
            className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-background"
            data-testid="see-it-in-action-section"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-14 items-center">
                <div className="lg:col-span-5">
                    <p className="label-eyebrow text-accent">See it in action</p>
                    <h2 className="font-heading text-4xl md:text-5xl mt-4 leading-[1.1]">
                        Watch one gala run, <em className="text-accent">end-to-end.</em>
                    </h2>
                    <p className="mt-6 text-foreground/70 text-lg leading-relaxed">
                        Real screenshots, real mock-data, real flow — silent auction (table sheets + mobile bidding), live auction (paddle raise + big-screen), door admission, POS, and the end-of-event report. No demo call required.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 mt-8">
                        <Link to="/tour" className="btn-accent" data-testid="hero-carousel-tour-cta">
                            <Play size={14} weight="fill" /> Take the live tour
                        </Link>
                        <a href="/api/marketing/gallery" target="_blank" rel="noreferrer" className="btn-outline" data-testid="hero-carousel-gallery-cta">
                            Browse all 35 screenshots <ArrowRight size={14} weight="bold" />
                        </a>
                    </div>

                    {/* Dots */}
                    <div className="flex gap-2 mt-10" data-testid="carousel-dots">
                        {SLIDES.map((_, idx) => (
                            <button
                                key={idx}
                                onClick={() => setI(idx)}
                                className={`h-1 transition-all ${idx === i ? "w-10 bg-accent" : "w-5 bg-border hover:bg-foreground/30"}`}
                                aria-label={`Slide ${idx + 1}`}
                                data-testid={`carousel-dot-${idx}`}
                            />
                        ))}
                    </div>
                </div>

                <div className="lg:col-span-7">
                    <div className="bg-card border border-border aspect-[16/10] overflow-hidden relative shadow-xl" data-testid="carousel-stage">
                        {SLIDES.map((slide, idx) => (
                            <div
                                key={slide.img}
                                className={`absolute inset-0 transition-opacity duration-700 ease-out ${idx === i ? "opacity-100 z-10" : "opacity-0 z-0"}`}
                                aria-hidden={idx !== i}
                            >
                                <img
                                    src={FILE(slide.img)}
                                    alt={slide.caption}
                                    className="w-full h-full object-cover"
                                />
                                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-primary/95 via-primary/55 to-transparent p-6 text-primary-foreground">
                                    <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-accent" data-testid={`carousel-eyebrow-${idx}`}>{slide.eyebrow}</p>
                                    <p className="font-heading text-2xl md:text-3xl mt-2 leading-tight" data-testid={`carousel-caption-${idx}`}>{slide.caption}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
